// The VFX registry (docs/ARCHITECTURE.md §8, docs/ART-DIRECTION.md §7): every effect is a data entry keyed by its
// id, built from one of six pooled primitives. Event bindings (bindings.ts) name effects by id; the primitives
// read only these definitions. Sizes: ring radii and spawn spreads are multiples of the placement's extent
// (radians); heights, widths and rises are render units; sprite sizes are angular (see SPRITE_VERTEX).

import type { ShapeCell } from './atlas'
import type { ColourKey } from './colours'
import type { PlacementKind } from './placement'
import type { RibbonStyle } from './RibbonPool'
import type { RingStyle } from './shaders'
import type { Envelope } from './timing'

export type Range = readonly [min: number, max: number]

export interface RingDef {
  readonly primitive: 'ring'
  readonly style: RingStyle
  readonly colour: ColourKey
  readonly intensity: number
  readonly delay: number
  readonly duration: number
  /** Outer angular radius over the lifetime (eased out), in extents. */
  readonly radius: Range
  /** Band width as a fraction of the outer radius; 1 = filled disc. */
  readonly width: number
  /** Height above the placement altitude over the lifetime (linear). */
  readonly altitude: Range
  /** Radial ticks per turn (wire style). */
  readonly dashes: number
  /** Glitch jitter of the radius (fraction), off under "Reduce effects". */
  readonly glitch: number
  readonly envelope: Envelope
  /** A bright, abrupt onset (ART-DIRECTION §4): it plays only within the screen-wide flash budget (FlashBudget). */
  readonly flash: boolean
  readonly maxConcurrent: number
}

export type SpriteShape = { readonly kind: 'shape'; readonly cell: ShapeCell } | { readonly kind: 'column'; readonly text: string }

export interface ParticleDef {
  /** One shape per particle, or a vertical word column (one glyph particle per character, moving together). */
  readonly shape: SpriteShape
  readonly colour: ColourKey
  readonly intensity: number
  /** Angular width and height (a column's per-character cell). */
  readonly size: readonly [width: number, height: number]
  readonly life: Range
  /** Random start delay, seconds. */
  readonly delay: Range
  /** Initial speed along the surface, outward from the anchor. */
  readonly speed: Range
  /** Initial radial speed (negative = falling). */
  readonly lift: Range
  /** Pull toward the globe centre, units / s². */
  readonly gravity: number
  /** Velocity damping per second. */
  readonly drag: number
  /** Spawn disc radius, in extents. */
  readonly spread: number
  /** Spawn height above the placement altitude. */
  readonly height: number
  /** Scale at birth and at death. */
  readonly scale: Range
  /** Rocking amplitude in radians (paper flutter); 0 = none. */
  readonly flutter: number
  /** Depth of the smooth FLICKER_HZ alpha flicker; 0 = none. Off under "Reduce effects". */
  readonly flicker: number
  /** Angular glitch jitter amplitude (pixel-sort); 0 = none. Off under "Reduce effects". */
  readonly jitter: number
}

export interface BurstDef {
  readonly primitive: 'burst'
  readonly particle: ParticleDef
  /** Particles (or columns) at High quality. */
  readonly count: number
}

export interface RainDef {
  readonly primitive: 'rain'
  readonly particle: ParticleDef
  /** Columns per second per anchor at High quality. */
  readonly columnsPerSecond: number
  readonly maxConcurrent: number
}

export type LabelText = { readonly kind: 'fixed'; readonly text: string } | { readonly kind: 'amount'; readonly prefix: string; readonly suffix: string }

export interface LabelDef {
  readonly primitive: 'label'
  readonly text: LabelText
  readonly colour: ColourKey
  readonly intensity: number
  readonly duration: number
  /** Height above the placement altitude at birth, and how far it rises over its life. */
  readonly height: number
  readonly rise: number
  /** Angular character height. */
  readonly charHeight: number
  readonly envelope: Envelope
  readonly maxConcurrent: number
}

/** arc = the missile flight arc (lifted like the strike glyph); surface = on the network; straight = chord. */
export type PathShape = 'arc' | 'surface' | 'straight'

export interface TrailDef {
  readonly primitive: 'trail'
  readonly path: PathShape
  readonly style: RibbonStyle
  readonly colour: ColourKey
  readonly intensity: number
  readonly delay: number
  readonly duration: number
  /** Head position along the path (0..1) at birth and at death (eased out). */
  readonly head: Range
  /** Tail length behind the head, as a path fraction. */
  readonly tail: number
  /** Tail brightness relative to the head. */
  readonly tailAlpha: number
  readonly envelope: Envelope
  /** A bright, abrupt onset (ART-DIRECTION §4): it plays only within the screen-wide flash budget (FlashBudget). */
  readonly flash: boolean
  readonly maxConcurrent: number
}

export interface FlightRibbon {
  readonly style: RibbonStyle
  readonly intensity: number
  /** Brightness at the end away from the missile (origin for the trail, target for the arc), relative to it. */
  readonly farAlpha: number
}

export interface FlightDef {
  readonly primitive: 'flight'
  readonly colour: ColourKey
  /** Travelled path, origin → missile. */
  readonly trail: FlightRibbon
  /** Dotted remaining arc, missile → target. */
  readonly arc: FlightRibbon
  /** Fade-out once the strike is resolved or gone. */
  readonly fadeSeconds: number
}

export type EffectDef = RingDef | BurstDef | RainDef | LabelDef | TrailDef | FlightDef
export type Primitive = EffectDef['primitive']

/** The placement each primitive plays at. */
export const PRIMITIVE_PLACEMENT = {
  ring: 'point',
  burst: 'point',
  rain: 'point',
  label: 'point',
  trail: 'path',
  flight: 'flight',
} as const satisfies Readonly<Record<Primitive, PlacementKind>>
export type PrimitivePlacement = typeof PRIMITIVE_PLACEMENT

export type DefOf<P extends Primitive> = Extract<EffectDef, { readonly primitive: P }>

/** When a played effect's flash begins (its delay), or null when the primitive or the entry is not a flash. */
const FLASH_ONSET: { readonly [P in Primitive]: (def: DefOf<P>) => number | null } = {
  ring: (def) => (def.flash ? def.delay : null),
  trail: (def) => (def.flash ? def.delay : null),
  burst: () => null,
  rain: () => null,
  label: () => null,
  flight: () => null,
}

export function flashOnsetDelay(def: EffectDef): number | null {
  return (FLASH_ONSET[def.primitive] as (def: EffectDef) => number | null)(def)
}

const NO_GLITCH = 0
const SOLID = { dashesPerUnit: 0, dashDuty: 1, scroll: 0 } as const
const IMPACT_SPARKS: ParticleDef = {
  shape: { kind: 'shape', cell: 'spark' },
  colour: 'spark',
  intensity: 1.4,
  size: [0.0045, 0.0045],
  life: [0.45, 0.9],
  delay: [0.05, 0.15],
  speed: [0.04, 0.14],
  lift: [0.01, 0.06],
  gravity: 0.18,
  drag: 2.2,
  spread: 0.25,
  height: 0.002,
  scale: [1, 0.3],
  flutter: 0,
  flicker: 0,
  jitter: 0,
}

/** Unit-attack landings: small, short-lived variants of the strike impact sparks. */
const HIT_SPARKS: ParticleDef = { ...IMPACT_SPARKS, size: [0.0025, 0.0025], life: [0.15, 0.3], delay: [0, 0.02], speed: [0.02, 0.05], lift: [0.005, 0.02], spread: 0.15, scale: [1, 0.2] }
const SHELL_DEBRIS: ParticleDef = { ...IMPACT_SPARKS, size: [0.0035, 0.0035], life: [0.3, 0.6], delay: [0, 0.03], speed: [0.03, 0.08], lift: [0.02, 0.05] }
const FLAK_SPARKS: ParticleDef = { ...IMPACT_SPARKS, size: [0.003, 0.003], life: [0.25, 0.45], delay: [0, 0.02], speed: [0.03, 0.07], lift: [0, 0.01], gravity: 0.05, spread: 0.1 }
const BOMB_FIRE: ParticleDef = { ...IMPACT_SPARKS, size: [0.004, 0.004], life: [0.4, 0.8], delay: [0, 0.05], speed: [0.01, 0.04], lift: [0.03, 0.07], gravity: 0.08, spread: 0.3 }

export const EFFECTS = {
  // --- Deploy: materialize ring rising bottom-up + "PURCHASE APPROVED ✓ −$700M" floater.
  deployMaterialize: {
    primitive: 'ring',
    style: 'wire',
    colour: 'owner',
    intensity: 1.2,
    delay: 0,
    duration: 0.9,
    radius: [0.6, 0.6],
    width: 0.12,
    altitude: [0, 0.02],
    dashes: 16,
    glitch: NO_GLITCH,
    envelope: { attack: 0.15, decay: 1 },
    flash: false,
    maxConcurrent: 8,
  },
  deployFloater: {
    primitive: 'label',
    text: { kind: 'amount', prefix: 'PURCHASE APPROVED ✓ −$', suffix: 'M' },
    colour: 'owner',
    intensity: 1.1,
    duration: 2.2,
    height: 0.03,
    rise: 0.03,
    charHeight: 0.016,
    envelope: { attack: 0.08, decay: 0.6 },
    maxConcurrent: 6,
  },
  // --- Launch: glitch ring at the launch city + streak lifting off along the flight arc.
  launchGlitchRing: {
    primitive: 'ring',
    style: 'wire',
    colour: 'owner',
    intensity: 1.3,
    delay: 0,
    duration: 0.7,
    radius: [0.3, 1.4],
    width: 0.15,
    altitude: [0, 0],
    dashes: 12,
    glitch: 0.35,
    envelope: { attack: 0.1, decay: 1.5 },
    flash: false,
    maxConcurrent: 6,
  },
  launchStreak: {
    primitive: 'trail',
    path: 'arc',
    style: { width: 0.006, core: 1, ...SOLID },
    colour: 'owner',
    intensity: 1.6,
    delay: 0,
    duration: 0.9,
    head: [0, 0.18],
    tail: 0.12,
    tailAlpha: 0,
    envelope: { attack: 0.05, decay: 2 },
    flash: false,
    maxConcurrent: 6,
  },
  // --- Flight: neon ribbon along the travelled great circle + dotted remaining altitude arc.
  strikeFlight: {
    primitive: 'flight',
    colour: 'owner',
    trail: { style: { width: 0.004, dashesPerUnit: 60, dashDuty: 0.6, scroll: 1.5, core: 0.5 }, intensity: 1.1, farAlpha: 0.15 },
    arc: { style: { width: 0.0025, dashesPerUnit: 140, dashDuty: 0.25, scroll: 0, core: 0 }, intensity: 0.45, farAlpha: 1 },
    fadeSeconds: 0.6,
  },
  // --- Interception: laser-thin beam, pixel-sort glitch burst, spark shower.
  interceptBeam: {
    primitive: 'trail',
    path: 'straight',
    style: { width: 0.0025, core: 1, ...SOLID },
    colour: 'text',
    intensity: 1.8,
    delay: 0,
    duration: 0.45,
    head: [1, 1],
    tail: 1,
    tailAlpha: 1,
    envelope: { attack: 0.05, decay: 1.5 },
    flash: true,
    maxConcurrent: 6,
  },
  interceptPixelSort: {
    primitive: 'burst',
    count: 18,
    particle: {
      shape: { kind: 'shape', cell: 'bar' },
      colour: 'text',
      intensity: 1.2,
      size: [0.012, 0.0022],
      life: [0.3, 0.55],
      delay: [0, 0.08],
      speed: [0, 0.01],
      lift: [0, 0.005],
      gravity: 0,
      drag: 4,
      spread: 1.2,
      height: 0,
      scale: [1, 0.6],
      flutter: 0,
      flicker: 0,
      jitter: 0.01,
    },
  },
  interceptSparks: {
    primitive: 'burst',
    count: 26,
    particle: { ...IMPACT_SPARKS, delay: [0, 0.05], spread: 0.3, height: 0 },
  },
  // --- Impact (tiered by strike type): flash core → surface shockwave → spark burst → fading damage static.
  impactCore: {
    primitive: 'ring',
    style: 'core',
    colour: 'hot',
    intensity: 1.6,
    delay: 0,
    duration: 0.5,
    radius: [0.3, 0.8],
    width: 1,
    altitude: [0.001, 0.001],
    dashes: 0,
    glitch: NO_GLITCH,
    envelope: { attack: 0.15, decay: 2 },
    flash: true,
    maxConcurrent: 8,
  },
  impactShockwave: {
    primitive: 'ring',
    style: 'wire',
    colour: 'warning',
    intensity: 1.1,
    delay: 0.05,
    duration: 0.9,
    radius: [0.2, 1],
    width: 0.12,
    altitude: [0.0005, 0.0005],
    dashes: 24,
    glitch: 0.1,
    envelope: { attack: 0.05, decay: 1.5 },
    flash: false,
    maxConcurrent: 8,
  },
  impactShockwaveEcho: {
    primitive: 'ring',
    style: 'wire',
    colour: 'warning',
    intensity: 0.8,
    delay: 0.3,
    duration: 1.1,
    radius: [0.1, 1.25],
    width: 0.08,
    altitude: [0.0005, 0.0005],
    dashes: 36,
    glitch: 0.1,
    envelope: { attack: 0.05, decay: 1.5 },
    flash: false,
    maxConcurrent: 6,
  },
  impactSparks: { primitive: 'burst', count: 30, particle: IMPACT_SPARKS },
  impactSparksHeavy: { primitive: 'burst', count: 60, particle: { ...IMPACT_SPARKS, speed: [0.06, 0.2] } },
  impactStatic: {
    primitive: 'ring',
    style: 'static',
    colour: 'danger',
    intensity: 0.5,
    delay: 0.3,
    duration: 4,
    radius: [0.8, 0.8],
    width: 1,
    altitude: [0.0003, 0.0003],
    dashes: 0,
    glitch: NO_GLITCH,
    envelope: { attack: 0.1, decay: 1 },
    flash: false,
    maxConcurrent: 8,
  },
  // --- Unit and structure attacks (vfx/attacks.ts): muzzle flashes and landings, sized by the firing unit's glyph (the
  // placement extent), with soft onsets so a firefight never counts against the flash budget.
  muzzleFlash: {
    primitive: 'ring',
    style: 'core',
    colour: 'hot',
    intensity: 0.9,
    delay: 0,
    duration: 0.16,
    radius: [0.12, 0.4],
    width: 1,
    altitude: [0, 0],
    dashes: 0,
    glitch: NO_GLITCH,
    envelope: { attack: 0.3, decay: 1.5 },
    flash: false,
    maxConcurrent: 32,
  },
  hitSpark: { primitive: 'burst', count: 3, particle: HIT_SPARKS },
  shellBlast: {
    primitive: 'ring',
    style: 'wire',
    colour: 'spark',
    intensity: 1,
    delay: 0,
    duration: 0.45,
    radius: [0.2, 0.9],
    width: 0.18,
    altitude: [0.0003, 0.0003],
    dashes: 12,
    glitch: NO_GLITCH,
    envelope: { attack: 0.1, decay: 1.5 },
    flash: false,
    maxConcurrent: 24,
  },
  shellDebris: { primitive: 'burst', count: 8, particle: SHELL_DEBRIS },
  flakBurst: {
    primitive: 'ring',
    style: 'wire',
    colour: 'spark',
    intensity: 1.1,
    delay: 0,
    duration: 0.35,
    radius: [0.1, 0.7],
    width: 0.3,
    altitude: [0, 0],
    dashes: 8,
    glitch: NO_GLITCH,
    envelope: { attack: 0.15, decay: 1.2 },
    flash: false,
    maxConcurrent: 16,
  },
  flakSparks: { primitive: 'burst', count: 10, particle: FLAK_SPARKS },
  bombBlast: {
    primitive: 'ring',
    style: 'core',
    colour: 'hot',
    intensity: 0.85,
    delay: 0,
    duration: 0.5,
    radius: [0.2, 1.1],
    width: 1,
    altitude: [0.0003, 0.0003],
    dashes: 0,
    glitch: NO_GLITCH,
    envelope: { attack: 0.25, decay: 1.8 },
    flash: false,
    maxConcurrent: 20,
  },
  bombFire: { primitive: 'burst', count: 10, particle: BOMB_FIRE },
  // --- Unit destroyed: hologram shatters into falling pixels + "SIGNAL LOST".
  unitShatter: {
    primitive: 'burst',
    count: 28,
    particle: {
      shape: { kind: 'shape', cell: 'pixel' },
      colour: 'owner',
      intensity: 1.2,
      size: [0.004, 0.004],
      life: [0.6, 1.1],
      delay: [0, 0.1],
      speed: [0.01, 0.05],
      lift: [0, 0.04],
      gravity: 0.05,
      drag: 1.2,
      spread: 0.6,
      height: 0.015,
      scale: [1, 0.4],
      flutter: 0,
      flicker: 0,
      jitter: 0,
    },
  },
  signalLostLabel: {
    primitive: 'label',
    text: { kind: 'fixed', text: 'SIGNAL LOST' },
    colour: 'owner',
    intensity: 1,
    duration: 1.6,
    height: 0.02,
    rise: 0.012,
    charHeight: 0.011,
    envelope: { attack: 0.1, decay: 0.8 },
    maxConcurrent: 8,
  },
  // --- City captured: new owner's colour sweeps around the city and along its edges.
  captureSweepRing: {
    primitive: 'ring',
    style: 'sweep',
    colour: 'owner',
    intensity: 1.2,
    delay: 0,
    duration: 1.2,
    radius: [0.9, 1.3],
    width: 0.2,
    altitude: [0.0005, 0.0005],
    dashes: 0,
    glitch: NO_GLITCH,
    envelope: { attack: 0.1, decay: 1 },
    flash: false,
    maxConcurrent: 6,
  },
  captureEdgeSweep: {
    primitive: 'trail',
    path: 'surface',
    style: { width: 0.003, dashesPerUnit: 80, dashDuty: 0.5, scroll: 2, core: 0.3 },
    colour: 'owner',
    intensity: 1,
    delay: 0,
    duration: 1.2,
    head: [0, 1],
    tail: 1,
    tailAlpha: 0.3,
    envelope: { attack: 0.1, decay: 1.5 },
    flash: false,
    maxConcurrent: 24,
  },
  // --- Sanctions: scrolling red INVOICE glyph rain over the target's cities.
  invoiceRain: {
    primitive: 'rain',
    columnsPerSecond: 1.5,
    maxConcurrent: 12,
    particle: {
      shape: { kind: 'column', text: 'INVOICE' },
      colour: 'danger',
      intensity: 1,
      size: [0.006, 0.006],
      life: [1.4, 1.6],
      delay: [0, 0],
      speed: [0, 0],
      lift: [-0.04, -0.04],
      gravity: 0,
      drag: 0,
      spread: 1.5,
      height: 0.06,
      scale: [1, 1],
      flutter: 0,
      flicker: 0,
      jitter: 0,
    },
  },
  // --- Leaflet drop: flickering paper glyphs fluttering down over the drop area.
  leafletFlutter: {
    primitive: 'burst',
    count: 36,
    particle: {
      shape: { kind: 'shape', cell: 'paper' },
      colour: 'text',
      intensity: 0.9,
      size: [0.008, 0.008],
      life: [2.2, 3.2],
      delay: [0, 1.2],
      speed: [0, 0.01],
      lift: [-0.015, -0.008],
      gravity: 0,
      drag: 0,
      spread: 1,
      height: 0.05,
      scale: [1, 0.9],
      flutter: 0.6,
      flicker: 0.35,
      jitter: 0,
    },
  },
} as const satisfies Readonly<Record<string, EffectDef>>

export type EffectId = keyof typeof EFFECTS

/** Effect ids that play at placements of kind P (the compiler rejects a binding that names the wrong kind). */
export type EffectIdFor<P extends PlacementKind> = {
  readonly [Id in EffectId]: PrimitivePlacement[(typeof EFFECTS)[Id]['primitive']] extends P ? Id : never
}[EffectId]

const EFFECT_IDS = Object.keys(EFFECTS) as readonly EffectId[]

export function effectDef(id: EffectId): EffectDef {
  return EFFECTS[id]
}

/** Every registry entry built from `primitive`, in registry order (pools reserve their slots from this). */
export function effectsOf<P extends Primitive>(primitive: P): readonly (readonly [EffectId, DefOf<P>])[] {
  const found: (readonly [EffectId, DefOf<P>])[] = []
  for (const id of EFFECT_IDS) {
    const def = effectDef(id)
    if (def.primitive === primitive) found.push([id, def as DefOf<P>])
  }
  return found
}
